import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Title3,
  Title2,
  Body1,
  Body2,
  Button,
  Spinner,
  makeStyles,
  tokens,
  MessageBar,
  MessageBarBody,
  Tooltip,
  Caption1
} from '@fluentui/react-components';
import {
  ArrowSync24Regular,
  Delete24Regular,
  Checkmark24Regular,
  Info24Regular,
  Clock24Regular,
  Database24Regular,
  ErrorCircle20Regular,
  Warning20Regular,
  DocumentSync20Regular,
  CalendarClock20Regular,
  PersonAdd20Regular,
  PersonDelete20Regular,
  PersonEdit20Regular,
  PeopleTeam20Regular,
  Building20Regular
} from '@fluentui/react-icons';
import { dlqService } from '../../services/dlqService';
import { snapshotService } from '../../services/snapshotService';
import type { BatchOperationResult, DlqOperationResponse, SnapshotMetricsResponse } from '../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  // New split cards layout
  splitCardsRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  splitCard: {
    padding: '16px',
  },
  splitCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  splitCardTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  splitCardIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '4px',
  },
  splitCardIconRed: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
  },
  splitCardIconGreen: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  splitStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '8px',
  },
  splitStatItem: {
    textAlign: 'center',
    padding: '6px 2px',
  },
  splitStatValue: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: tokens.colorBrandForeground1,
  },
  splitStatValueRed: {
    color: tokens.colorPaletteRedForeground1,
  },
  splitStatValueGreen: {
    color: tokens.colorPaletteGreenForeground1,
  },
  splitStatLabel: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    marginTop: '2px',
  },
  // Original styles
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
  },
  statCard: {
    padding: '16px',
    textAlign: 'center',
  },
  statCardHealthy: {
    borderLeft: `4px solid ${tokens.colorPaletteGreenBorder1}`,
  },
  statCardWarning: {
    borderLeft: `4px solid ${tokens.colorPaletteYellowBorder1}`,
  },
  statCardCritical: {
    borderLeft: `4px solid ${tokens.colorPaletteRedBorder1}`,
  },
  statValue: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: tokens.colorBrandForeground1,
  },
  statValueLarge: {
    fontSize: '36px',
    fontWeight: 'bold',
  },
  statValueHealthy: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statValueWarning: {
    color: tokens.colorPaletteYellowForeground1,
  },
  statValueCritical: {
    color: tokens.colorPaletteRedForeground1,
  },
  statLabel: {
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
  },
  sectionTitle: {
    marginTop: '24px',
    marginBottom: '12px',
  },
  chartContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '16px',
  },
  distributionCard: {
    padding: '16px',
  },
  distributionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '12px',
  },
  distributionItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 10px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '5px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  distributionItemLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    minWidth: 0,
  },
  distributionIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '5px',
    flexShrink: 0,
  },
  distributionIconBlue: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  distributionIconRed: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
  },
  distributionIconYellow: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground1,
  },
  distributionIconGreen: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  distributionLabel: {
    fontSize: '11px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  distributionItemRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  distributionCount: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    minWidth: '35px',
    textAlign: 'right',
  },
  distributionBar: {
    width: '70px',
    height: '5px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '2px',
    overflow: 'hidden',
  },
  distributionBarFill: {
    height: '100%',
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  sectionDivider: {
    marginTop: '24px',
    marginBottom: '8px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingBottom: '8px',
  },
  // Manter estilos antigos para compatibilidade
  barContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '16px',
  },
  barItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  barLabel: {
    minWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  barTrack: {
    flex: 1,
    height: '24px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '4px',
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    backgroundColor: tokens.colorBrandBackground,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingRight: '8px',
    color: 'white',
    fontSize: '12px',
    fontWeight: 'bold',
    minWidth: '40px',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    gap: '16px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    gap: '16px',
    color: tokens.colorNeutralForeground3,
  },
  trendIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  sampleBadge: {
    marginLeft: '8px',
  },
  metaInfo: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '8px',
  },
  progressBar: {
    height: '8px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: tokens.colorBrandBackground,
    transition: 'width 0.3s ease-in-out',
  },
  progressStats: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
});

interface DlqDashboardOptimizedProps {
  onRefresh?: () => void;
}

export const DlqDashboardOptimized: React.FC<DlqDashboardOptimizedProps> = ({ onRefresh }) => {
  const styles = useStyles();
  const [snapshot, setSnapshot] = useState<SnapshotMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [operationResult, setOperationResult] = useState<BatchOperationResult | null>(null);
  const [operationLoading, setOperationLoading] = useState<string | null>(null);
  const [operationMessage, setOperationMessage] = useState<string>('');
  const [activeOperation, setActiveOperation] = useState<DlqOperationResponse | null>(null);
  const [triggerLoading, setTriggerLoading] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Usar o novo endpoint de snapshot (dados completos, sem amostragem)
      const data = await snapshotService.getMetrics();
      setSnapshot(data);
    } catch (err: any) {
      console.error('Error loading snapshot metrics:', err);
      // Se não encontrou snapshot (404), não mostrar erro - mostrar estado vazio
      if (err.response?.status === 404 || err.message?.includes('404')) {
        setSnapshot(null);
      } else {
        setError('Erro ao carregar métricas do snapshot. Verifique se a API está disponível.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Dispara captura de novo snapshot e aguarda conclusão
  const handleTriggerSnapshot = async () => {
    try {
      setTriggerLoading(true);
      setError(null);
      setOperationMessage('Iniciando captura do snapshot...');

      await snapshotService.triggerCapture();

      // Aguarda o snapshot ser completado via polling
      await snapshotService.pollUntilComplete(
        (metrics) => {
          setSnapshot(metrics);
          setOperationMessage(`Capturando... Status: ${metrics.status}`);
        },
        3000, // 3 segundos de intervalo
        60    // 3 minutos máximo
      );

      setOperationMessage('');
      onRefresh?.();
    } catch (err: any) {
      console.error('Error triggering snapshot:', err);
      // Tratar diferentes tipos de erro
      if (err.message?.includes('timeout')) {
        setError('Captura do snapshot demorou mais que o esperado. Tente novamente em alguns minutos.');
      } else if (err.message?.includes('VITE_SNAPSHOT_FUNCTION_URL')) {
        setError('URL da Function de snapshot não configurada. Verifique as variáveis de ambiente.');
      } else {
        setError(`Erro ao capturar snapshot: ${err.message}`);
      }
      setOperationMessage('');
      // Tentar recarregar mesmo assim - snapshot pode ter sido criado
      loadDashboard();
    } finally {
      setTriggerLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleReprocessByType = async (messageType: string) => {
    if (!confirm(`Tem certeza que deseja reprocessar TODAS as mensagens do tipo "${messageType}"?`)) {
      return;
    }

    try {
      setOperationLoading(messageType);
      const result = await dlqService.reprocessByMessageType(messageType);
      setOperationResult(result);
      loadDashboard();
      onRefresh?.();
    } catch (err) {
      console.error('Error reprocessing messages:', err);
      setError('Erro ao reprocessar mensagens');
    } finally {
      setOperationLoading(null);
    }
  };

  const handleDeleteByPattern = async (pattern: string, estimatedCount?: number) => {
    const countText = estimatedCount ? ` (~${estimatedCount.toLocaleString('pt-BR')} mensagens)` : '';
    if (!confirm(`Tem certeza que deseja DELETAR PERMANENTEMENTE todas as mensagens com erro "${pattern}"?${countText}\n\nEsta operação será executada em segundo plano. Você pode acompanhar o progresso ou sair da página.`)) {
      return;
    }

    try {
      setOperationLoading(pattern);
      setOperationMessage(`Iniciando operação de delete para "${pattern}"...`);
      setError(null);
      setOperationResult(null);

      // Inicia operação assíncrona
      const operation = await dlqService.startDeleteByPatternAsync(pattern);
      setActiveOperation(operation);
      setOperationMessage(`Operação iniciada. ID: ${operation.id}`);

      // Polling para acompanhar progresso
      await dlqService.pollOperation(
        operation.id,
        (updatedOp) => {
          setActiveOperation(updatedOp);
          setOperationMessage(
            `Progresso: ${updatedOp.processed.toLocaleString('pt-BR')} deletadas, ` +
            `${updatedOp.failed.toLocaleString('pt-BR')} falharam, ` +
            `${updatedOp.skipped.toLocaleString('pt-BR')} ignoradas ` +
            `(${updatedOp.progressPercentage}%) - ${updatedOp.durationFormatted}`
          );
        },
        2000, // 2s de intervalo
        600   // 20 minutos máximo
      );

      // Operação concluída
      const finalOp = await dlqService.getOperationStatus(operation.id);
      setActiveOperation(finalOp);
      setOperationResult({
        processed: finalOp.processed,
        failed: finalOp.failed,
        total: finalOp.processed + finalOp.failed + finalOp.skipped,
        allSucceeded: finalOp.status === 'Completed' && finalOp.failed === 0
      });
      setOperationMessage('');
      loadDashboard();
      onRefresh?.();
    } catch (err) {
      console.error('Error in delete operation:', err);
      setError(`Erro na operação de delete. Verifique o status em "Operações Recentes".`);
      setOperationMessage('');
      loadDashboard();
    } finally {
      setOperationLoading(null);
    }
  };

  const getTopItems = (record: Record<string, number> | undefined, limit: number = 10) => {
    if (!record) return [];
    return Object.entries(record)
      .sort(([, a], [, b]) => b - a)
      .slice(0, limit);
  };

  const getMaxValue = (items: [string, number][]) => {
    return Math.max(...items.map(([, v]) => v), 1);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" />
        <Body1>Carregando dashboard da DLQ...</Body1>
        <Caption1>Usando métricas otimizadas (instantâneo)</Caption1>
      </div>
    );
  }

  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{error}</MessageBarBody>
      </MessageBar>
    );
  }

  if (!snapshot) {
    return (
      <div className={styles.emptyState}>
        <Database24Regular style={{ fontSize: '48px', color: tokens.colorNeutralForeground3 }} />
        <Title3>Nenhum Snapshot Disponível</Title3>
        <Body1>Ainda não há dados de snapshot das filas. Clique em "Capturar Snapshot" para gerar.</Body1>
        <Button
          appearance="primary"
          icon={triggerLoading ? <Spinner size="tiny" /> : <ArrowSync24Regular />}
          onClick={handleTriggerSnapshot}
          disabled={triggerLoading}
        >
          {triggerLoading ? 'Capturando...' : 'Capturar Snapshot'}
        </Button>
        {triggerLoading && operationMessage && (
          <Caption1>{operationMessage}</Caption1>
        )}
      </div>
    );
  }

  const { metrics } = snapshot;
  const hasDeadLetters = metrics.dlqCount > 0;
  const hasScheduledMessages = metrics.scheduledCount > 0;
  const hasAnyMessages = hasDeadLetters || hasScheduledMessages;

  // Formatar bytes
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Se não há mensagens em nenhuma fila (nem DLQ nem Agendadas)
  if (!hasAnyMessages) {
    return (
      <div className={styles.emptyState}>
        <Checkmark24Regular style={{ fontSize: '48px', color: tokens.colorPaletteGreenForeground1 }} />
        <Title3>Filas Vazias</Title3>
        <Body1>Não há mensagens na Dead Letter Queue nem mensagens agendadas.</Body1>
        <div className={styles.metaInfo}>
          <span className={styles.metaItem}>
            <Clock24Regular />
            Capturado: {new Date(snapshot.capturedAt).toLocaleString('pt-BR')}
          </span>
          <span className={styles.metaItem}>
            <Database24Regular />
            Duração: {snapshot.duration}
          </span>
        </div>
        <Button
          appearance="primary"
          icon={triggerLoading ? <Spinner size="tiny" /> : <ArrowSync24Regular />}
          onClick={handleTriggerSnapshot}
          disabled={triggerLoading}
        >
          {triggerLoading ? 'Atualizando...' : 'Atualizar'}
        </Button>
      </div>
    );
  }

  // Usar dados reais do snapshot (100% das mensagens, sem amostragem)
  const messageTypes = getTopItems(metrics.dlqByMessageType);
  const errorPatterns = getTopItems(metrics.dlqByErrorPattern);
  const scheduledTypes = getTopItems(metrics.scheduledByMessageType);
  const scheduledErrorPatterns = getTopItems(metrics.scheduledByErrorPattern);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Title2>Queue Dashboard</Title2>
        <Button
          appearance="primary"
          icon={triggerLoading ? <Spinner size="tiny" /> : <ArrowSync24Regular />}
          onClick={handleTriggerSnapshot}
          disabled={triggerLoading}
        >
          {triggerLoading ? 'Atualizando...' : 'Atualizar'}
        </Button>
      </div>

      {/* Operation In Progress with Progress Bar */}
      {operationLoading && activeOperation && (
        <Card className={styles.progressContainer}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Spinner size="tiny" />
            <Body1>
              {activeOperation.type === 'DeleteByPattern' && `Deletando "${activeOperation.pattern}"...`}
              {activeOperation.type === 'Purge' && 'Limpando toda a DLQ...'}
              {activeOperation.type === 'ReprocessByMessageType' && `Reprocessando "${activeOperation.pattern}"...`}
            </Body1>
          </div>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${activeOperation.progressPercentage}%` }}
            />
          </div>
          <div className={styles.progressStats}>
            <span>
              {activeOperation.processed.toLocaleString('pt-BR')} processadas
              {activeOperation.failed > 0 && `, ${activeOperation.failed.toLocaleString('pt-BR')} falharam`}
              {activeOperation.skipped > 0 && `, ${activeOperation.skipped.toLocaleString('pt-BR')} ignoradas`}
            </span>
            <span>
              {activeOperation.progressPercentage}% • {activeOperation.durationFormatted}
            </span>
          </div>
          <Caption1>
            Operação ID: {activeOperation.id} • Status: {activeOperation.status}
          </Caption1>
        </Card>
      )}

      {/* Operation Loading without Active Operation (starting...) */}
      {operationLoading && !activeOperation && operationMessage && (
        <MessageBar intent="warning">
          <MessageBarBody>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Spinner size="tiny" />
              <span>{operationMessage}</span>
            </div>
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Operation Result */}
      {operationResult && !operationLoading && (
        <MessageBar intent={operationResult.allSucceeded ? 'success' : 'warning'}>
          <MessageBarBody>
            Operação concluída: {operationResult.processed.toLocaleString('pt-BR')} processadas, {operationResult.failed.toLocaleString('pt-BR')} falharam
            {activeOperation && ` (${activeOperation.durationFormatted})`}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Trigger Loading */}
      {triggerLoading && operationMessage && (
        <MessageBar intent="info">
          <MessageBarBody>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Spinner size="tiny" />
              <span>{operationMessage}</span>
            </div>
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Stats Cards - Split DLQ vs Agendadas */}
      <div className={styles.splitCardsRow}>
        {/* DLQ Stats Card */}
        <Card className={styles.splitCard}>
          <div className={styles.splitCardHeader}>
            <div className={`${styles.splitCardIcon} ${styles.splitCardIconRed}`}>
              <ErrorCircle20Regular />
            </div>
            <span className={styles.splitCardTitle}>Dead Letter Queue</span>
          </div>
          <div className={styles.splitStatsGrid}>
            <div className={styles.splitStatItem}>
              <div className={`${styles.splitStatValue} ${styles.splitStatValueRed}`}>
                {metrics.dlqCount.toLocaleString('pt-BR')}
              </div>
              <div className={styles.splitStatLabel}>Mensagens</div>
            </div>
            <div className={styles.splitStatItem}>
              <div className={styles.splitStatValue}>
                {Object.keys(metrics.dlqByMessageType || {}).length}
              </div>
              <div className={styles.splitStatLabel}>Tipos</div>
            </div>
            <div className={styles.splitStatItem}>
              <div className={styles.splitStatValue}>
                {Object.keys(metrics.dlqByErrorPattern || {}).length}
              </div>
              <div className={styles.splitStatLabel}>Erros</div>
            </div>
            <div className={styles.splitStatItem}>
              <div className={styles.splitStatValue}>
                {formatBytes(metrics.dlqSizeBytes || 0)}
              </div>
              <div className={styles.splitStatLabel}>Tamanho</div>
            </div>
          </div>
        </Card>

        {/* Scheduled Stats Card */}
        <Card className={styles.splitCard}>
          <div className={styles.splitCardHeader}>
            <div className={`${styles.splitCardIcon} ${styles.splitCardIconGreen}`}>
              <CalendarClock20Regular />
            </div>
            <span className={styles.splitCardTitle}>Mensagens Agendadas</span>
          </div>
          <div className={styles.splitStatsGrid}>
            <div className={styles.splitStatItem}>
              <div className={`${styles.splitStatValue} ${styles.splitStatValueGreen}`}>
                {metrics.scheduledCount.toLocaleString('pt-BR')}
              </div>
              <div className={styles.splitStatLabel}>Mensagens</div>
            </div>
            <div className={styles.splitStatItem}>
              <div className={styles.splitStatValue}>
                {Object.keys(metrics.scheduledByMessageType || {}).length}
              </div>
              <div className={styles.splitStatLabel}>Tipos</div>
            </div>
            <div className={styles.splitStatItem}>
              <div className={styles.splitStatValue}>
                {Object.keys(metrics.scheduledByErrorPattern || {}).length}
              </div>
              <div className={styles.splitStatLabel}>Erros</div>
            </div>
            <div className={styles.splitStatItem}>
              <div className={styles.splitStatValue}>
                {formatBytes(metrics.scheduledSizeBytes || 0)}
              </div>
              <div className={styles.splitStatLabel}>Tamanho</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Meta Info */}
      <div className={styles.metaInfo}>
        <span className={styles.metaItem}>
          <Clock24Regular />
          Capturado: {new Date(snapshot.capturedAt).toLocaleString('pt-BR')}
        </span>
        <span className={styles.metaItem}>
          <Database24Regular />
          Duração: {snapshot.duration}
        </span>
        <span className={styles.metaItem}>
          <Info24Regular />
          Trigger: {snapshot.triggeredBy}
        </span>
        {metrics.activeCount > 0 && (
          <span className={styles.metaItem}>
            <ArrowSync24Regular />
            Ativas: {metrics.activeCount.toLocaleString('pt-BR')}
          </span>
        )}
      </div>

      {/* DLQ Section - only show if there are DLQ messages */}
      {hasDeadLetters && (
        <>
          <div className={styles.sectionDivider}>
            <Title3>Dead Letter Queue</Title3>
          </div>

          <div className={styles.chartContainer}>
        {/* DLQ - By Message Type */}
        <Card className={styles.distributionCard}>
          <Body1 style={{ fontWeight: 600, marginBottom: '4px' }}>Por Tipo de Operação</Body1>
          <div className={styles.distributionList}>
            {messageTypes.length === 0 ? (
              <Body2 style={{ color: tokens.colorNeutralForeground3, padding: '12px' }}>Nenhum dado disponível</Body2>
            ) : (
              messageTypes.map(([type, count]) => {
                const maxVal = getMaxValue(messageTypes);
                const percentage = (count / maxVal) * 100;
                return (
                  <div key={type} className={styles.distributionItem}>
                    <div className={styles.distributionItemLeft}>
                      <div className={`${styles.distributionIcon} ${styles.distributionIconBlue}`}>
                        {getOperationIcon(type)}
                      </div>
                      <span className={styles.distributionLabel}>{type}</span>
                    </div>
                    <div className={styles.distributionItemRight}>
                      <div className={styles.distributionBar}>
                        <div
                          className={styles.distributionBarFill}
                          style={{ width: `${percentage}%`, backgroundColor: tokens.colorBrandBackground }}
                        />
                      </div>
                      <span className={styles.distributionCount}>{count.toLocaleString('pt-BR')}</span>
                      <Tooltip content="Reprocessar" relationship="label">
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<ArrowSync24Regular />}
                          onClick={() => handleReprocessByType(type)}
                          disabled={!!operationLoading}
                        />
                      </Tooltip>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>

        {/* DLQ - By Error Pattern */}
        <Card className={styles.distributionCard}>
          <Body1 style={{ fontWeight: 600, marginBottom: '4px' }}>Por Tipo de Erro</Body1>
          <div className={styles.distributionList}>
            {errorPatterns.length === 0 ? (
              <Body2 style={{ color: tokens.colorNeutralForeground3, padding: '12px' }}>Nenhum dado disponível</Body2>
            ) : (
              errorPatterns.map(([pattern, count]) => {
                const maxVal = getMaxValue(errorPatterns);
                const percentage = (count / maxVal) * 100;
                const isRecoverable = ['Timeout', 'Rate Limited', 'Service Unavailable', 'Retry Limit Exceeded', 'Queue Disabled'].includes(pattern);
                return (
                  <div key={pattern} className={styles.distributionItem}>
                    <div className={styles.distributionItemLeft}>
                      <div className={`${styles.distributionIcon} ${isRecoverable ? styles.distributionIconYellow : styles.distributionIconRed}`}>
                        {isRecoverable ? <Warning20Regular /> : <ErrorCircle20Regular />}
                      </div>
                      <span className={styles.distributionLabel}>{pattern}</span>
                    </div>
                    <div className={styles.distributionItemRight}>
                      <div className={styles.distributionBar}>
                        <div
                          className={styles.distributionBarFill}
                          style={{
                            width: `${percentage}%`,
                            backgroundColor: isRecoverable ? tokens.colorPaletteYellowBackground2 : tokens.colorPaletteRedBackground2
                          }}
                        />
                      </div>
                      <span className={styles.distributionCount}>{count.toLocaleString('pt-BR')}</span>
                      <Tooltip content="Deletar" relationship="label">
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={operationLoading === pattern ? <Spinner size="tiny" /> : <Delete24Regular />}
                          onClick={() => handleDeleteByPattern(pattern, count)}
                          disabled={!!operationLoading}
                        />
                      </Tooltip>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>
      </div>
        </>
      )}

      {/* Scheduled Section */}
      {metrics.scheduledCount > 0 && (
        <>
          <div className={styles.sectionDivider}>
            <Title3>Mensagens Agendadas</Title3>
          </div>

          <div className={styles.chartContainer}>
            {/* Scheduled - By Message Type */}
            <Card className={styles.distributionCard}>
              <Body1 style={{ fontWeight: 600, marginBottom: '4px' }}>Por Tipo de Operação</Body1>
              <div className={styles.distributionList}>
                {scheduledTypes.length === 0 ? (
                  <Body2 style={{ color: tokens.colorNeutralForeground3, padding: '12px' }}>Nenhum dado disponível</Body2>
                ) : (
                  scheduledTypes.map(([type, count]) => {
                    const maxVal = getMaxValue(scheduledTypes);
                    const percentage = (count / maxVal) * 100;
                    return (
                      <div key={type} className={styles.distributionItem}>
                        <div className={styles.distributionItemLeft}>
                          <div className={`${styles.distributionIcon} ${styles.distributionIconGreen}`}>
                            {getOperationIcon(type)}
                          </div>
                          <span className={styles.distributionLabel}>{type}</span>
                        </div>
                        <div className={styles.distributionItemRight}>
                          <div className={styles.distributionBar}>
                            <div
                              className={styles.distributionBarFill}
                              style={{ width: `${percentage}%`, backgroundColor: tokens.colorPaletteGreenBackground2 }}
                            />
                          </div>
                          <span className={styles.distributionCount}>{count.toLocaleString('pt-BR')}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Card>

            {/* Scheduled - By Error Pattern */}
            <Card className={styles.distributionCard}>
              <Body1 style={{ fontWeight: 600, marginBottom: '4px' }}>Por Tipo de Erro</Body1>
              <div className={styles.distributionList}>
                {scheduledErrorPatterns.length === 0 ? (
                  <Body2 style={{ color: tokens.colorNeutralForeground3, padding: '12px' }}>Nenhum erro registrado</Body2>
                ) : (
                  scheduledErrorPatterns.map(([pattern, count]) => {
                    const maxVal = getMaxValue(scheduledErrorPatterns);
                    const percentage = (count / maxVal) * 100;
                    const isRecoverable = ['Timeout', 'Rate Limited', 'Service Unavailable', 'Retry Limit Exceeded', 'Queue Disabled'].includes(pattern);
                    return (
                      <div key={pattern} className={styles.distributionItem}>
                        <div className={styles.distributionItemLeft}>
                          <div className={`${styles.distributionIcon} ${isRecoverable ? styles.distributionIconYellow : styles.distributionIconRed}`}>
                            {isRecoverable ? <Warning20Regular /> : <ErrorCircle20Regular />}
                          </div>
                          <span className={styles.distributionLabel}>{pattern}</span>
                        </div>
                        <div className={styles.distributionItemRight}>
                          <div className={styles.distributionBar}>
                            <div
                              className={styles.distributionBarFill}
                              style={{
                                width: `${percentage}%`,
                                backgroundColor: isRecoverable ? tokens.colorPaletteYellowBackground2 : tokens.colorPaletteRedBackground2
                              }}
                            />
                          </div>
                          <span className={styles.distributionCount}>{count.toLocaleString('pt-BR')}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

// Helper function to get icon based on operation type
function getOperationIcon(type: string) {
  if (type.toLowerCase().includes('new') || type.toLowerCase().includes('create')) {
    return <PersonAdd20Regular />;
  }
  if (type.toLowerCase().includes('delete') || type.toLowerCase().includes('remove')) {
    return <PersonDelete20Regular />;
  }
  if (type.toLowerCase().includes('update') || type.toLowerCase().includes('edit')) {
    return <PersonEdit20Regular />;
  }
  if (type.toLowerCase().includes('section') || type.toLowerCase().includes('class')) {
    return <PeopleTeam20Regular />;
  }
  if (type.toLowerCase().includes('school')) {
    return <Building20Regular />;
  }
  return <DocumentSync20Regular />;
}

export default DlqDashboardOptimized;
